/* eslint-disable max-lines-per-function */
import { useMemo } from 'react';

import { VisitHistoryDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { createTranslatedText } from '@dreryk/react-common';
import {
   AlignItems,
   CheckIcon,
   createDynamicColumnsWidth,
   DataConfiguration,
   IconFillColor,
   IconSize,
   IconWrapper,
   JustifyContent,
   SecondaryText,
   StyledFlex,
   TableColumnConfiguration,
   Text,
   ValueRendererProps,
} from '@dreryk/react-components';

import { visitHistoryTranslationKeys } from '../translations';

export const useVisitHistoryDiagnosisDataConfiguration = (
   data: VisitHistoryDiagnosis[]
): DataConfiguration<VisitHistoryDiagnosis> => {
   return useMemo(() => ({ data: data }), [data]);
};

export const useVisitHistoryDiagnosisColumnsConfiguration = (): TableColumnConfiguration<VisitHistoryDiagnosis>[] => {
   return useMemo<TableColumnConfiguration<VisitHistoryDiagnosis>[]>(() => {
      const columnsWidth = createDynamicColumnsWidth({
         icd10: '112px',
         diseaseName: '100%',
         main: '68px',
         chronic: '89px',
      });

      return [
         {
            label: visitHistoryTranslationKeys.diagnosisTable.icd10,
            name: 'icd10Code',
            width: columnsWidth.icd10,
            accessor: 'icd10Code',
            sortingDisabled: true,
         },
         {
            label: visitHistoryTranslationKeys.diagnosisTable.diseaseName,
            name: 'name',
            width: columnsWidth.diseaseName,
            accessor: 'name',
            sortingDisabled: true,

            renderer: ({
               bean: { name, additionalInformation },
            }: ValueRendererProps<VisitHistoryDiagnosis, string>): JSX.Element => {
               return (
                  <StyledFlex direction={'column'}>
                     <Text testId={'name'}>{name}</Text>
                     {additionalInformation ? (
                        <SecondaryText
                           testId={'additionalInformation'}
                           text={createTranslatedText(additionalInformation)}
                        />
                     ) : null}
                  </StyledFlex>
               );
            },
         },
         {
            label: visitHistoryTranslationKeys.diagnosisTable.main,
            width: columnsWidth.main,
            sortingDisabled: true,
            justifyContent: JustifyContent.CENTER,
            alignItems: AlignItems.CENTER,
            accessor: 'main',

            renderer: ({ value: main }: ValueRendererProps<VisitHistoryDiagnosis, boolean>): JSX.Element | null => {
               if (!main) {
                  return null;
               }

               return (
                  <IconWrapper size={IconSize.SMALL} testId={'main'} fillColor={IconFillColor.PRIMARY}>
                     <CheckIcon />
                  </IconWrapper>
               );
            },
         },
         {
            label: visitHistoryTranslationKeys.diagnosisTable.chronic,
            width: columnsWidth.chronic,
            sortingDisabled: true,
            justifyContent: JustifyContent.CENTER,
            alignItems: AlignItems.CENTER,
            accessor: 'chronic',

            renderer: ({ value: chronic }: ValueRendererProps<VisitHistoryDiagnosis, boolean>): JSX.Element | null => {
               if (!chronic) {
                  return null;
               }

               return (
                  <IconWrapper size={IconSize.SMALL} testId={'chronic'} fillColor={IconFillColor.PRIMARY}>
                     <CheckIcon />
                  </IconWrapper>
               );
            },
         },
      ];
   }, []);
};
